import { useMemo, useRef } from 'react';
import { resolveHomeBandLayout } from '../../../lib/catch-up-layout';
import { useShowMobileAppCard } from '../../../lib/mobile-app-status';
import { HOME_STACK_BREAKPOINT_PX, useContainerNarrow } from '../../../lib/use-container-narrow';
import { useNarrow } from '../../../lib/use-narrow';
import { useOpportunities } from '../../../lib/opportunities';
import { usePiledUpJobs } from '../../../lib/piled-up-jobs';
import { useWorkspaces } from '../../../lib/workspaces';
import { FilesSummaryCard } from '../agents/FilesSummaryCard';
import { ConnectToolsCard } from './ConnectToolsCard';
import { OpportunityPile } from './OpportunityPile';
import { PiledUpJobs } from './PiledUpJobs';
import { WorkspacesPanel } from './WorkspacesPanel';
import styles from './CatchUpSections.module.css';

// The home's middle band, under the header and above the dashboard (Figma "Catch Up states" 7419:47452).
//
// ONE row of at most three slots — what the agents finished overnight (the tasks strip), what they are
// proposing next (the ideas pile), and the connect-tools callout — and, under it, the workspaces row:
// the user's spaces on the left beside a Files entry on the right, at the design's ⅔ : ⅓. Every slot is
// optional and each row's width follows whichever survive; see `resolveHomeBandLayout` and
// `.workspacesRow`.
//
// The download-the-app QR moved UP into the header beside the brief (`DesktopHomeHeader`), so it is no
// longer a band slot.
//
// The opportunities pile expands IN PLACE inside its own slot (it owns that state) — "View More" grows
// the slot into the full list and "View Less" collapses it back, so the band never relocates.

/** One shared empty array for every not-loaded-yet source, so a pending fetch doesn't hand its child a
 *  fresh reference (and re-run the memo) on every render. */
const NONE: never[] = [];

export function CatchUpSections() {
  // `.sections` is the `@container` the two rows below stack on, so it is also what decides whether the
  // strip/pile render their desktop or their phone form — one measurement, handed down, instead of a
  // `matchMedia` query in each child that cannot see the docked chat. See `PiledUpJobs`.
  const sectionsRef = useRef<HTMLDivElement>(null);
  const narrow = useContainerNarrow(sectionsRef, HOME_STACK_BREAKPOINT_PX);
  const jobs = usePiledUpJobs() ?? NONE;
  const opportunities = useOpportunities() ?? NONE;
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : NONE;

  // Where the connect-tools callout goes. It is offered ALWAYS — connecting a tool is a standing errand,
  // not a one-off onboarding step — but it has exactly ONE home on the screen, and which one depends on
  // whether the QR is also being offered (the two share the header's ⅓ column):
  //
  //   • QR shown     → the QR keeps the header slot, so the callout rides the band as its third slot,
  //                    and the tasks strip piles up to fit the narrower track (Figma 15545:336847).
  //   • QR not shown → `DesktopHomeHeader` renders the callout in the QR's place and the band is left
  //                    exactly as it was — no third slot, no piling.
  //
  // `headerHasCard` mirrors that header decision rather than re-deciding it, which is what keeps the
  // card from rendering twice. It has to read the WINDOW width, not this container's: `HomeHeader`
  // picks Desktop-vs-Mobile on `useNarrow`, so with a docked chat the row here can be stacked while the
  // DESKTOP header above it is still holding the callout in its ⅓ column.
  const showQr = useShowMobileAppCard();
  const windowNarrow = useNarrow();
  const headerHasCard = !windowNarrow && !showQr;
  const connect = headerHasCard ? null : <ConnectToolsCard />;

  const nameOf = useMemo(() => {
    const byId = new Map(workspaces.map((ws) => [ws.id, ws.name]));
    return (opportunity: { workspaceId: string }) => byId.get(opportunity.workspaceId);
  }, [workspaces]);

  const { showWorkspaces, showBand, columns } = resolveHomeBandLayout(
    workspaces.length,
    {
      hasJobs: jobs.length > 0,
      hasOpportunities: opportunities.length > 0,
      // In the stacked column the callout is a row of its own, not a band slot — see the narrow branch.
      hasConnect: !narrow && connect !== null,
    },
    // The workspaces-row threshold keys on the true VIEWPORT (mobile web shows the row from one
    // workspace, matching native; desktop needs two), NOT the docked-chat container squeeze above.
    windowNarrow,
  );

  // Stacked, the callout is a row of its own, so it alone is enough to keep the section mounted.
  const stackedConnectOnly = narrow && connect !== null;
  if (!showBand && !showWorkspaces && !stackedConnectOnly) return null;

  const tasks =
    jobs.length > 0 ? <PiledUpJobs jobs={jobs} narrow={narrow} piled={!narrow && connect !== null} /> : null;
  const opportunitiesPile =
    opportunities.length > 0 ? (
      <OpportunityPile opportunities={opportunities} workspaceName={nameOf} narrow={narrow} />
    ) : null;
  const spaces = showWorkspaces ? <WorkspacesPanel workspaces={workspaces} /> : null;
  const files = showWorkspaces ? <FilesSummaryCard pageName="home" compact /> : null;

  // Stacked (narrow / docked-chat) layout: one column in the NATIVE + Figma order — the connect
  // callout, the tasks strip, then the workspaces carousel, then the opportunities pile (iOS
  // `SymphonyHomeView`, Android `Pages`). Desktop keeps opportunities beside the tasks strip in the
  // band, so this reorder — moving the workspaces carousel ABOVE opportunities — is scoped to the
  // single-column layout. The Files tile (web-only; native's home has no Files card) trails at the foot.
  //
  // The callout LEADS the column, directly under the brief card that `MobileHomeHeader` ends on and
  // ABOVE the piled-up jobs — the phone frame's own order. It is the first thing the eye reaches after
  // the brief; below the pile it sat far enough down the scroll to read as an afterthought.
  if (narrow) {
    return (
      <div className={styles.sections} ref={sectionsRef}>
        {connect && <div className={styles.narrowConnect}>{connect}</div>}
        {tasks}
        {spaces}
        {opportunitiesPile}
        {files}
      </div>
    );
  }

  return (
    <div className={styles.sections} ref={sectionsRef}>
      {showBand && (
        <div className={styles.band} style={{ gridTemplateColumns: columns }}>
          {tasks}
          {opportunitiesPile}
          {/* Bottom-aligned in the row, so the callout's foot lands level with the cards beside it
              rather than with the section HEADERS above them (Figma 15545:337754 sits at y=35 of the
              220 row, i.e. flush with the 185-tall cards). */}
          {connect && <div className={styles.connectSlot}>{connect}</div>}
        </div>
      )}
      {showWorkspaces && (
        <div className={styles.workspacesRow}>
          {spaces}
          {files}
        </div>
      )}
    </div>
  );
}
